import { notification } from "antd";
import api from "../services/api";
import * as texts from "../pages/Users/locales";

const getFilenameFromDisposition = (contentDisposition = "", fallback: any) => {
  if (!contentDisposition) {
    return fallback;
  }

  try {
    const utf8FilenameMatch = contentDisposition.match(
      /filename\*=UTF-8''([^;]+)/i
    );
    if (utf8FilenameMatch && utf8FilenameMatch[1]) {
      return decodeURIComponent(utf8FilenameMatch[1]);
    }

    const asciiFilenameMatch = contentDisposition.match(
      /filename="?([^";]+)"?/i
    );
    if (asciiFilenameMatch && asciiFilenameMatch[1]) {
      return asciiFilenameMatch[1];
    }
  } catch (error) {
    // Fallback to provided name if parsing fails
  }

  return fallback;
};

const normalizeIdentifier = (value: any) => {
  if (value == null) {
    return null;
  }

  if (typeof value === "string") {
    return value.trim().length > 0 ? value : null;
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    const nested = value._id || value.id;
    if (typeof nested === "string" && nested.trim().length > 0) {
      return nested;
    }

    if (typeof nested === "number") {
      return String(nested);
    }
  }

  return null;
};

export const getApiErrorMessage = (error: any) => {
  const { response, message } = error ?? {};
  const data = response?.data;

  const extractString = (value: any) =>
    typeof value === "string" && value.trim().length > 0 ? value : null;

  const extracted =
    extractString(data) ||
    extractString(data?.message) ||
    extractString(data?.error) ||
    (Array.isArray(data?.errors)
      ? extractString(
          data.errors
            .filter(
              (item: any) => typeof item === "string" && item.trim().length
            )
            .join(", ")
        )
      : null) ||
    extractString(message);

  return extracted || null;
};

export const getRecordWalletId = (record: any) =>
  record?.paymentMethodId ||
  record?.payment_method_id ||
  record?.wallet_id ||
  record?.walletId ||
  record?.paymentMethod?._id ||
  null;

export const extractIdentifier = (...values: any) => {
  for (const value of values) {
    const identifier = normalizeIdentifier(value);
    if (identifier) {
      return identifier;
    }
  }

  return null;
};

export const handleDownloadRefundReceipt = async ({
  record,
  wallet_id,
  user_id,
  freire,
  transaction_id = null,
}: {
  record: any;
  wallet_id: any;
  user_id: any;
  freire: any;
  transaction_id?: any;
}) => {
  const refundResourceId = transaction_id ?? extractIdentifier(record?._id);

  if (!refundResourceId) {
    notification.error({
      message: freire(texts.OOPS),
      description: freire(texts.ERROR_DOWNLOAD_REFUND_RECEIPT),
    });
    return;
  }

  try {
    const recordWalletId = wallet_id || getRecordWalletId(record);
    if (!recordWalletId) {
      throw new Error("Missing wallet/payment method ID for receipt");
    }
    const response = await api.get(
      `/admin/users/${user_id}/wallets/${recordWalletId}/transactions/${refundResourceId}/refund-voucher`,
      {
        responseType: "blob",
      }
    );

    const { data, headers } = response;
    const blob = new Blob([data], {
      type: headers["content-type"] || "application/pdf",
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const filename = getFilenameFromDisposition(
      headers["content-disposition"],
      `refund-receipt-${refundResourceId}.pdf`
    );

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    const fallbackMessage = freire(texts.ERROR_DOWNLOAD_REFUND_RECEIPT);
    const endpointMessage = getApiErrorMessage(error);

    notification.error({
      message: freire(texts.OOPS),
      description: (
        <div>
          <div>{fallbackMessage}</div>
          {endpointMessage && endpointMessage !== fallbackMessage ? (
            <div style={{ marginTop: 4 }}>{endpointMessage}</div>
          ) : null}
        </div>
      ),
    });
  }
};
